import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { ArrowLeft, Plus, Search, Upload, Eye, Download, Trash2, FileText, X } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useAutoRefresh } from '@/hooks/use-auto-refresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ExecutiveBriefingModule({ user, moduleName, moduleTitle }) {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [uploadingReportId, setUploadingReportId] = useState(null);
  const [viewPdfData, setViewPdfData] = useState(null);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  useEffect(() => {
    fetchReports();
  }, []);

  useEffect(() => {
    filterReports();
  }, [searchQuery, statusFilter, reports]);

  const fetchReports = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/executive-briefing-reports?module=${moduleName}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data);
      setFilteredReports(response.data);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied');
      }
    }
  };

  // Auto-refresh every 5 seconds when not editing
  const isUserEditing = createDialogOpen || uploadingPdf || deleteDialogOpen || showPdfViewer || isCreating;
  useAutoRefresh(fetchReports, 5, isUserEditing);

  const filterReports = () => {
    let filtered = reports;

    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(report =>
        getReportTitle(report).toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(report => report.status === statusFilter);
    }

    setFilteredReports(filtered);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('ALL');
  };

  const formatDateRange = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    const startDay = format(startDate, 'do');
    const endDay = format(endDate, 'do');
    const month = format(endDate, 'MMM');
    const year = format(endDate, 'yyyy');
    
    return `${startDay} - ${endDay} ${month}, ${year}`;
  };

  const getReportTitle = (report) => {
    return `Executive Briefing Report - ${report.module} - ${formatDateRange(report.start_date, report.end_date)}`;
  };

  const handleCreateReport = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select start and end dates');
      return;
    }

    if (new Date(endDate) < new Date(startDate)) {
      toast.error('End date must be after start date');
      return;
    }

    setIsCreating(true);
    
    try {
      const token = localStorage.getItem('token');
      const data = {
        module: moduleName,
        start_date: startDate,
        end_date: endDate
      };

      await axios.post(`${API}/executive-briefing-reports/create`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });

      toast.success('Report created successfully');
      setCreateDialogOpen(false);
      setStartDate('');
      setEndDate('');
      fetchReports();
      
    } catch (error) {
      console.error('Failed to create report:', error);
      toast.error(error.response?.data?.detail || 'Failed to create report');
    } finally {
      setIsCreating(false);
    }
  };

  const handleInlineFileSelect = (reportId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        if (!file.type.includes('pdf')) {
          toast.error('Please select a PDF file');
          return;
        }
        handleUploadPdf(reportId, file);
      }
    };
    input.click();
  };

  const handleUploadPdf = async (reportId, file) => {
    setUploadingPdf(true);
    setUploadingReportId(reportId);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/executive-briefing-reports/${reportId}/upload-pdf`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('PDF uploaded successfully');
      setUploadingReportId(null);
      fetchReports();
    } catch (error) {
      console.error('Failed to upload PDF:', error);
      toast.error('Failed to upload PDF');
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleViewPdf = async (report) => {
    if (!report.pdf_url) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}${report.pdf_url}`);
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        setViewPdfData({
          url: reader.result,
          title: getReportTitle(report)
        });
        setShowPdfViewer(true);
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      console.error('Failed to load PDF:', error);
      toast.error('Failed to load PDF');
    }
  };

  const handleDownloadPdf = (report) => {
    if (report.pdf_url) {
      const link = document.createElement('a');
      link.href = `${BACKEND_URL}${report.pdf_url}`;
      link.download = `${getReportTitle(report)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDeleteClick = (report) => {
    setReportToDelete(report);
    setDeleteDialogOpen(true);
  };

  const handleDeletePdfClick = (report) => {
    setReportToDelete(report);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!reportToDelete) return;

    try {
      const token = localStorage.getItem('token');
      
      if (reportToDelete.pdf_url) {
        // Delete only the PDF, keep the report
        await axios.delete(`${API}/executive-briefing-reports/${reportToDelete.id}/pdf`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('PDF deleted successfully. Report status changed to Pending.');
      } else {
        // Delete the entire report
        await axios.delete(`${API}/executive-briefing-reports/${reportToDelete.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Report deleted successfully');
      }

      setDeleteDialogOpen(false);
      setReportToDelete(null);
      fetchReports();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Failed to delete');
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'Completed') {
      return 'bg-green-100 text-green-800';
    }
    return 'bg-yellow-100 text-yellow-800';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b-2 border-black">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/task-management/executive-briefing')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {moduleTitle}
                </h1>
                <p className="text-sm text-gray-600 mt-1">Executive Briefing Reports</p>
              </div>
            </div>
            <Button onClick={() => setCreateDialogOpen(true)} className="bg-black hover:bg-gray-800">
              <Plus className="w-4 h-4 mr-2" />
              Create Report
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        {/* Search and Filters */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search reports..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <SearchableSelect
            options={[
              { value: 'ALL', label: 'All Status' },
              { value: 'Pending', label: 'Pending' },
              { value: 'Completed', label: 'Completed' }
            ]}
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder="Filter by status"
          />
        </div>

        {/* Reports Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredReports.map((report) => (
            <div key={report.id} className="bg-white border-2 border-black p-4 hover:shadow-lg transition-shadow">
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <h3 className="font-bold text-lg">{formatDateRange(report.start_date, report.end_date)}</h3>
                  <p className="text-sm text-gray-600">{report.module}</p>
                </div>
                <span className={`px-2 py-1 text-xs font-bold rounded ${getStatusBadge(report.status)}`}>
                  {report.status}
                </span>
              </div>

              <div className="flex gap-2 mt-4">
                {!report.pdf_url ? (
                  <>
                    <Button
                      onClick={() => handleInlineFileSelect(report.id)}
                      size="sm"
                      className="flex-1 bg-black hover:bg-gray-800"
                      disabled={uploadingPdf && uploadingReportId === report.id}
                    >
                      <Upload className="w-4 h-4 mr-1" />
                      {uploadingPdf && uploadingReportId === report.id ? 'Uploading...' : 'Upload PDF'}
                    </Button>
                    <Button
                      onClick={() => handleDeleteClick(report)}
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      onClick={() => handleViewPdf(report)}
                      size="sm"
                      variant="outline"
                      className="flex-1"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    <Button
                      onClick={() => handleDownloadPdf(report)}
                      size="sm"
                      variant="outline"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleDeletePdfClick(report)}
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {filteredReports.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">
              {reports.length === 0 ? 'No reports found' : 'No reports match your filters'}
            </p>
            <p className="text-sm text-gray-500 mt-2">
              {reports.length === 0 ? 'Create your first executive briefing report' : 'Try adjusting your filters'}
            </p>
          </div>
        )}
      </main>

      {/* Create Report Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Executive Briefing Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Start Date</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">End Date</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <p className="text-sm text-gray-500">After creating the report, upload the PDF from the grid.</p>
          </div>
          <DialogFooter>
            <Button 
              type="button"
              variant="outline" 
              onClick={() => setCreateDialogOpen(false)}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleCreateReport}
              disabled={isCreating}
              className="bg-black hover:bg-gray-800"
            >
              {isCreating ? 'Creating...' : 'Create Report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Viewer Dialog */}
      {showPdfViewer && viewPdfData && (
        <Dialog open={showPdfViewer} onOpenChange={(open) => {
          setShowPdfViewer(open);
          if (!open) setViewPdfData(null);
        }}>
          <DialogContent className="max-w-screen max-h-screen w-screen h-screen p-0 m-0 rounded-none border-0">
            <DialogHeader className="absolute top-0 left-0 right-0 z-10 px-6 py-3 bg-white border-b">
              <div className="flex items-center justify-between">
                <DialogTitle>{viewPdfData.title}</DialogTitle>
                <button 
                  onClick={() => setShowPdfViewer(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
            </DialogHeader>
            {viewPdfData && (
              <iframe
                src={viewPdfData.url}
                className="w-full h-full border-0"
                title="Executive Briefing Report PDF"
                style={{ marginTop: '60px' }}
                type="application/pdf"
              />
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {reportToDelete?.pdf_url ? 'Delete PDF' : 'Delete Report'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 mb-4">
            {reportToDelete?.pdf_url 
              ? 'Are you sure you want to delete the PDF? The report will remain but status will change to Pending.'
              : 'Are you sure you want to delete this report? This action cannot be undone.'
            }
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
